import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { withSlider } from '../context/SliderContext';

import Close from '../components/icons/close';
import Layout from '../components/layout';
import SEO from '../components/seo';

import RecipeSteps from '../components/recipe/steps/';

SwiperCore.use([Navigation]);

type Props = {
  setSlide: (slide: number) => {};
};

const LexiquePage = ({ setSlide }: Props) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    setSlide(6);
  }, []);

  return (
    <Layout>
      <SEO
        bodyClass="lexique"
        description="Le dictionnaire culinaire pour déchiffrer n’importe quelle recette."
        facebook="/social/facebook/7.lexique.png"
        slug="/lexique"
        twitter="/social/twitter/7.lexique.png"
        title="Les astuces du chef."
      />
      <Link className="recipe-back-link" to="/" title="Retour a l'accueil">
        <Close fill="#1550c3" stroke="#ffdfed" />
      </Link>
      <div className="lexique-wrapper">
        <RecipeSteps current={step} length={4} />
        <Swiper navigation slidesPerView={1} onSlideChange={event => setStep(event.activeIndex)}>
          <SwiperSlide>
            <div className="lexique-content recipe-sticker recipe-7-sticker">
              <p>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;T’as
                tes propres expressions, en cuisine c’est pareil. On t’a donc traduit quelques mots
                et expressions pour que tu puisses survivre aux recettes de n’importe quel bouquin.
              </p>
              <ul>
                <li>
                  <p className="title">Abaisser / Foncer</p>
                  <p>
                    « Abaisser » c’est quand tu étales de la pâte avec un rouleau à pâtisserie pour
                    faire une tarte par exemple. Alors que « foncer » c’est quand tu garnis un moule
                    (de pâte).
                  </p>
                </li>
                <li>
                  <p className="title">Appareil</p>
                  <p>
                    C’est le mélange d’ingrédients qui va te servir à faire une préparation, souvent
                    en pâtisserie mais aussi quand tu fais des tartes et des cakes salés. Ça aurait
                    été plus simple de dire mélange, on est d'accord.
                  </p>
                </li>
                <li>
                  <p className="title">Arroser</p>
                  <p>
                    C’est verser du liquide avec une cuillère sur une viande pendant la cuisson pour
                    qu’elle ne soit pas sèche. Tu peux le faire avec le jus de la viande, du beurre
                    fondu, de l’eau…{' '}
                  </p>
                </li>
              </ul>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="lexique-content">
              <ul>
                <li>
                  <p className="title">Bain-marie</p>
                  <p>
                    C’est un mode de cuisson qui permet de cuire et de faire fondre doucement les
                    aliments. On fait bouillir l’eau dans une grande casserole remplie au 1/3. On
                    fait ensuite flotter une plus petite casserole dedans pour cuire tranquillement
                    les aliments à l’intérieur.
                  </p>
                </li>
                <li>
                  <p className="title">Blanchir</p>
                  <p>
                    C’est mettre un aliment comme des haricots dans de l’eau bouillante un court
                    instant, puis les plonger dans de l’eau très froide.
                  </p>
                </li>
                <li>
                  <p className="title">Déglacer</p>
                  <p>
                    C’est verser un liquide dans une poêle à la fin de la cuisson d’une viande, pour
                    que tout ce qui est collé au fond fasse une sauce avec beaucoup de goût. Tu peux
                    le faire avec de l’eau, de la crème, du vinaigre balsamique…
                  </p>
                </li>
                <li>
                  <p className="title">Emincer</p>
                  <p>C’est simplement couper en fines tranches ou fines lamelles.</p>
                </li>
              </ul>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="lexique-content">
              <ul>
                <li>
                  <p className="title">Faire mijoter</p>
                  <p>C’est faire cuire très lentement un aliment dans un jus ou dans sa sauce.</p>
                </li>
                <li>
                  <p className="title">Faire réduire</p>
                  <p>
                    C’est laisser ta casserole avec un liquide sur le feu pour que la quantité
                    diminue. Plus ça diminue, plus ça devient épais.
                  </p>
                </li>
                <li>
                  <p className="title">Faire sauter</p>
                  <p>
                    Tu peux faire sauter des oignons, des échalotes ou certaines viandes. C’est les
                    faire cuire rapidement, puissance au max, dans un peu de beurre ou d’huile.
                  </p>
                </li>
                <li>
                  <p className="title">Féculents complets</p>
                  <p>
                    Ce sont les féculents élaborés à partir de céréales complètes et non raffinées
                    (avoine, riz, seigle, orge, sarrasin).  Ils existent presque tous en version
                    complète : pain complet, au son, aux céréales, pâtes alimentaires complètes, riz
                    complet, farine de blé complet, de sarrasin, etc. C’est bon pour ton palais et
                    pour ta santé.
                  </p>
                </li>
              </ul>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="lexique-content">
              <ul>
                <li>
                  <p className="title">Mouiller à hauteur</p>
                  <p>
                    C’est un peu différent d’arroser, c’est le fait de recouvrir entièrement ta
                    préparation de liquide pour la faire cuire ou en faire une sauce.
                  </p>
                </li>
                <li>
                  <p className="title">Réserver</p>
                  <p>C’est mettre de côté avec un mot plus classe.</p>
                </li>
                <li>
                  <p className="title">Saisir</p>
                  <p>
                    C’est faire cuire à feu très vif la partie extérieure d’un aliment. Comme avec
                    un steak, grillé à l’extérieur, et saignant à l’intérieur. Ça donne faim cette
                    histoire.
                  </p>
                </li>
              </ul>
            </div>
          </SwiperSlide>
        </Swiper>
         {' '}
      </div>
    </Layout>
  );
};

export default withSlider(LexiquePage);
